/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import { readCookie } from '@tcp/core/src/utils/cookie.util';
import { API_CONFIG } from '@tcp/core/src/services/config';
import { getSessionStorage, setSessionStorage } from '@tcp/core/src/utils';
import { dataLayer as defaultDataLayer } from '@tcp/core/src/analytics';
import { getPageNameValue } from '@tcp/core/src/analytics/utils';
import { getUserLoggedInState } from '@tcp/core/src/components/features/account/User/container/User.selectors';
import {
  generateBrowseDataLayer,
  generateHomePageDataLayer,
  generateClickHandlerDataLayer,
} from './dataLayers';

import { getSetSessionStorageObj } from './dataLayers/browse.dataLayer';

const getCartCheckoutPageType = (pageType) => {
  return pageType === 'checkout' || pageType === 'shopping bag';
};

const getCartTypeforBossSwitch = (personalData) => {
  let cartType = 'boss|checkout switch';
  if (personalData?.isExpressEligible) {
    cartType = 'boss|checkout switch|Instant Checkout';
  }
  return cartType;
};

const getEligibilityForInstant = (isPlccICFired, paymentsList, isExpressAfterpayDone) => {
  return (
    (isPlccICFired &&
      paymentsList?.paymentMethod &&
      (paymentsList.paymentMethod.toLowerCase() === 'placecard' ||
        paymentsList.paymentMethod.toLowerCase() === 'place card')) ||
    isExpressAfterpayDone
  );
};

/**
 * Analytics data layer object for property lookups.
 *
 * This function will create an object for the analytics
 * script to reference when mapping data elements. This
 * object should be assigned to the global context
 * (e.g., window) for easy access.
 *
 * @example
 * // In the app source
 * global._dataLater = create(store);
 * // In the analytics script
 * _dataLater.pageName; // "gl:home"
 *
 * @param {Object} store reference to the Redux store
 * @returns {Object} data layer
 */
export default function create(store) {
  const browseDataLayer = generateBrowseDataLayer(store);
  const homepageDataLayer = generateHomePageDataLayer(store);
  const clickHandlerDataLayer = generateClickHandlerDataLayer(store);
  const siteType = 'global site';
  const { pageCountCookieKey, previousPageValue } = API_CONFIG;
  const getStatePropValue = (propName) => {
    const { pageData, AnalyticsDataKey } = store.getState();
    const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
    const propValue = clickActionAnalyticsData[propName]
      ? clickActionAnalyticsData[propName]
      : pageData[propName];

    return propValue || '';
  };

  const getEvar97 = () => {
    const { AnalyticsDataKey, pageData } = store.getState();
    const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
    const { pageType } = pageData;
    let navText = '';
    if (
      pageType === 'browse' ||
      pageType === 'browse-hlp' ||
      pageType === 'browse-aplp' ||
      (clickActionAnalyticsData && clickActionAnalyticsData.pageType === 'browse')
    ) {
      navText =
        clickActionAnalyticsData.pageNavigationText ||
        clickActionAnalyticsData.pageNavigationText === ''
          ? clickActionAnalyticsData.pageNavigationText
          : pageData.pageNavigationText;
    }
    return navText;
  };

  const getOrderTypes = (shipmentsData) => {
    const orderTypes = [];
    for (let i = 0; i < shipmentsData.length; i += 1) {
      const obj = shipmentsData[i];
      let shipmentMethod = (obj.shippingMethod && obj.shippingMethod.name.toLowerCase()) || '';
      if (shipmentMethod.includes('boss')) {
        shipmentMethod = 'boss';
      } else if (shipmentMethod.includes('bopis')) {
        shipmentMethod = 'bopis';
      } else {
        shipmentMethod = 'standard';
      }
      if (shipmentMethod && !orderTypes.includes(shipmentMethod)) {
        orderTypes.push(shipmentMethod);
      }
    }
    return orderTypes;
  };

  return Object.create(defaultDataLayer, {
    ...browseDataLayer,
    ...homepageDataLayer,
    ...clickHandlerDataLayer,
    pageCount: {
      get() {
        return readCookie(pageCountCookieKey);
      },
    },
    pageName: {
      get() {
        return getPageNameValue(store.getState());
      },
    },
    orderId: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData');
        return clickActionAnalyticsData && clickActionAnalyticsData.orderId
          ? clickActionAnalyticsData.orderId
          : pageData.orderId;
      },
    },
    paymentMethod: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData');
        const analyticsPaymentMethod =
          (clickActionAnalyticsData && clickActionAnalyticsData.paymentMethod) ||
          pageData.paymentMethod;
        if (analyticsPaymentMethod) return analyticsPaymentMethod;
        const orderObject = store.getState()?.Confirmation?.get('orderConfirmation') || {};
        const orderObjPayment = (orderObject?.paymentsList && orderObject?.paymentsList[0]) || {};
        return (orderObjPayment && orderObjPayment.paymentMethod) || '';
      },
    },
    billingCountry: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData');
        return clickActionAnalyticsData && clickActionAnalyticsData.billingCountry
          ? clickActionAnalyticsData.billingCountry
          : pageData.billingCountry;
      },
    },
    billingZip: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData');
        return clickActionAnalyticsData && clickActionAnalyticsData.billingZip
          ? clickActionAnalyticsData.billingZip
          : pageData.billingZip;
      },
    },
    orderSubtotal: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData');
        return clickActionAnalyticsData && clickActionAnalyticsData.orderSubtotal
          ? clickActionAnalyticsData.orderSubtotal
          : pageData.orderSubtotal;
      },
    },
    isCurrentRoute: () => false,

    pageShortName: {
      get() {
        /* If clickActionAnalyticsData has pageShortName then this will be used else
           pageShortName will used from pageData. Also if pageShortName is not available then pageName will
           be used. This is usually require when on some event you need
           to override the pageName value. For instance, onClick event.
         */
        const { pageData, AnalyticsDataKey } = store.getState();

        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        const pageShortName = clickActionAnalyticsData.pageShortName
          ? clickActionAnalyticsData.pageShortName
          : pageData.pageShortName;
        const pageName = clickActionAnalyticsData.pageName
          ? clickActionAnalyticsData.pageName
          : pageData.pageName;
        return `${pageShortName || pageName}`;
      },
    },

    pageType: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        const pageType = clickActionAnalyticsData.pageType
          ? clickActionAnalyticsData.pageType
          : pageData.pageType;
        const pageName = clickActionAnalyticsData.pageName
          ? clickActionAnalyticsData.pageName
          : pageData.pageName;
        return `${pageType || pageName}`;
      },
    },
    pageUrl: {
      get() {
        return `https://${document.location.hostname}${document.location.pathname}`;
      },
    },

    countryId: {
      get() {
        return store.getState().APIConfig.storeId;
      },
    },

    pageLocale: {
      get() {
        const { session = {} } = store.getState();
        const { siteDetails } = session;
        return `${siteDetails.country.toLowerCase()}:${siteDetails.language.toLowerCase()}`;
      },
    },

    pageSection: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.pageSection
          ? clickActionAnalyticsData.pageSection
          : pageData.pageSection;
      },
    },

    pageSubSubSection: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.pageSubSection
          ? clickActionAnalyticsData.pageSubSection
          : pageData.pageSubSection;
      },
    },

    pageTertiarySection: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.pageTertiarySection
          ? clickActionAnalyticsData.pageTertiarySection
          : pageData.pageTertiarySection;
      },
    },

    siteType: {
      get() {
        return siteType;
      },
    },

    customerType: {
      get() {
        return getUserLoggedInState(store.getState()) ? 'no rewards:logged in' : 'no rewards:guest';
      },
    },

    checkoutType: {
      get() {
        const state = store.getState();
        const { pageType = '' } = state.pageData;
        const cartCheckoutPageType = getCartCheckoutPageType(pageType);
        let userType = '';
        if (cartCheckoutPageType) {
          const personalData = state.User.get('personalData');
          userType = personalData && personalData.get('isGuest') === false ? 'registered' : 'guest';
        }
        return userType;
      },
    },

    userEmailAddress: {
      get() {
        return store.getState().User.getIn(['personalData', 'contactInfo', 'emailAddress'], '');
      },
    },

    giftType: {
      get() {
        const clickAnalyticsData = store
          .getState()
          .AnalyticsDataKey.get('clickActionAnalyticsData', {});
        const giftType = getSessionStorage('PREFERRED_GIFT_SERVICE') || '';
        return clickAnalyticsData.confirmationPage ? giftType : '';
      },
    },

    currencyCode: {
      get() {
        return store.getState().session.siteDetails.currency.toUpperCase();
      },
    },

    pageDate: {
      get() {
        return new Date().toISOString().split('T')[0];
      },
    },

    customerId: {
      get() {
        const { AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.customerId
          ? clickActionAnalyticsData.customerId
          : store.getState().User.getIn(['personalData', 'userId'], '');
      },
    },

    customerFirstName: {
      get() {
        return store.getState().User.getIn(['personalData', 'contactInfo', 'firstName'], '');
      },
    },

    customerLastName: {
      get() {
        return store.getState().User.getIn(['personalData', 'contactInfo', 'lastName'], '');
      },
    },

    productId: {
      get() {
        return getStatePropValue('productId');
      },
    },
    eventAction: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eventAction'], '');
      },
    },
    eventCategory: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eventCategory'], '');
      },
    },

    pageNavigationText: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const { pageType } = pageData;
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return (
          (pageType !== 'product' &&
            (clickActionAnalyticsData.pageNavigationText ||
            clickActionAnalyticsData.pageNavigationText === ''
              ? clickActionAnalyticsData.pageNavigationText
              : pageData.pageNavigationText)) ||
          ''
        );
      },
    },

    cartType: {
      get() {
        const { pageName = '' } = store.getState().pageData;
        const { isPlccICFired = false, isExpressAfterpayDone } = JSON.parse(
          JSON.stringify(store.getState().AddedToBagReducer || {})
        );
        const { personalData = {} } = JSON.parse(JSON.stringify(store?.getState().User));
        const { isBossCartFromSwitch = false } = JSON.parse(
          JSON.stringify(store?.getState().CartItemTileReducer)
        );

        let cartType = '';
        const instantCheckoutConstant = 'Instant Checkout';
        if (pageName === 'checkout:confirmation') {
          cartType = 'standard';
          const orderObject = store.getState()?.Confirmation?.get('orderConfirmation') || {};
          const shipmentsData = orderObject && orderObject.shipments;
          const paymentsList =
            (orderObject &&
              orderObject.paymentsList &&
              orderObject.paymentsList.length &&
              orderObject.paymentsList[0]) ||
            {};
          if (!shipmentsData) return '';
          const orderTypes = getOrderTypes(shipmentsData);
          if (orderTypes.length > 1) {
            cartType = 'mix';
          } else if (orderTypes.length === 1) {
            const [type] = orderTypes;
            cartType = type;
          }
          if (getEligibilityForInstant(isPlccICFired, paymentsList, isExpressAfterpayDone)) {
            cartType = cartType.concat('|', instantCheckoutConstant);
          }
          if (isBossCartFromSwitch) {
            cartType = getCartTypeforBossSwitch(personalData);
          }
        }
        return cartType;
      },
    },

    products: {
      get() {
        const { AnalyticsDataKey, pageData } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};

        const pageProducts = clickActionAnalyticsData.products
          ? clickActionAnalyticsData.products
          : pageData.products;

        return pageProducts || [];
      },
    },

    currentState: {
      get() {
        return store.getState();
      },
    },

    storeId: {
      get() {
        const { AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.storeId;
      },
    },
    brandId: {
      get() {
        const { brandId = '' } = store.getState().APIConfig;
        return brandId.toUpperCase();
      },
    },
    landingSiteBrandId: {
      get() {
        const { landingSite } = API_CONFIG;
        return readCookie(landingSite) || '';
      },
    },
    pageErrorType: {
      get() {
        const { AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.pageErrorType;
      },
    },
    pageErrorValues: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'pageErrorValues'], '');
      },
    },
    appDownloadLocation: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'appDownloadLocation'], '');
      },
    },
    essentialsFormValue: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'essentialsFormValue']);
      },
    },
    Click_Navigation_v97: {
      get() {
        return getEvar97() || '';
      },
    },
    eVar97FirstTouch: {
      get() {
        const analyticsObj = getSetSessionStorageObj('e97', false);
        const ftVal = (analyticsObj && analyticsObj.e97FirstTouch) || '';
        const flag = (analyticsObj && analyticsObj.flag) || false;
        if (ftVal !== '' && flag) return '';

        const hasL2 = store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'hasL2'], false);
        const hasL3 = store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'hasL3'], false);
        const ev97 = !hasL2 && !hasL3 ? getEvar97() : '';
        if (ev97 !== '' && !hasL2 && !hasL3) {
          const obj = {
            e97FirstTouch: ev97,
            flag: true,
          };
          getSetSessionStorageObj('e97', true, obj);
        }
        return ev97;
      },
    },
    eVar28: {
      get() {
        let prevValue = '';
        const pageName = getPageNameValue(store.getState());
        const sessionVal = getSessionStorage(previousPageValue) || '';
        if (sessionVal) {
          prevValue = sessionVal;
          if (prevValue !== pageName) {
            setSessionStorage({ key: previousPageValue, value: pageName });
          }
        } else if (pageName) {
          setSessionStorage({ key: previousPageValue, value: pageName });
        }
        return prevValue;
      },
    },
    mprItem: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'mprItem'], '');
      },
    },
    plccItem: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'plccItem'], '');
      },
    },
    guestItem: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'guestItem'], '');
      },
    },
    metric86: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric86'], '');
      },
    },
    getAppleTrackValues: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'applePayButtonLocation'], '');
      },
    },
    orderHelpLocation: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'orderHelpLocation'], '');
      },
    },
    orderId_cd127: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'orderId_cd127'], '');
      },
    },
    orderHelp_path_cd128: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'orderHelp_path_cd128'], '');
      },
    },
    metric112: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric112'], '');
      },
    },
    metric115: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric115'], '');
      },
    },
    loyaltyLocation: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'loyaltyLocation']);
      },
    },
    afterpay_metrics141: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'afterpay_metrics141'], '');
      },
    },
    afterpay_cd131: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'afterpay_cd131'], '');
      },
    },
    bopis_multistore_cd133: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_multistore_cd133'], '');
      },
    },
    promotion_metrics17: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'promotion_metrics17'], '');
      },
    },
    promotion_cd55: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'promotion_cd55'], '');
      },
    },
    promotion_cd56: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'promotion_cd56'], '');
      },
    },
    promotion_cd58: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'promotion_cd58'], '');
      },
    },
    backpack_cd11: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'backpack_cd11'], '');
      },
    },
    new_filter_cd50: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'new_filter_cd50'], '');
      },
    },
    new_filter_cd161: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'new_filter_cd161'], '');
      },
    },
    new_filter_sort_cd51: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'new_filter_sort_cd51'], '');
      },
    },
    new_filter_sort_cd174: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'new_filter_sort_cd174'], '');
      },
    },
    color_consolidation_cd157: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'color_consolidation_cd157'], '');
      },
    },
    color_consolidation_metrics104: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(
            ['clickActionAnalyticsData', 'color_consolidation_metrics104'],
            ''
          );
      },
    },
    backpack_metrics31: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'backpack_metrics31'], '');
      },
    },
    backpack_metrics32: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'backpack_metrics32'], '');
      },
    },
    bopis_filter_metrics167: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_filter_metrics167'], '');
      },
    },
    bopis_filter_metrics168: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_filter_metrics168'], '');
      },
    },
    bopis_filter_metrics169: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_filter_metrics169'], '');
      },
    },
    syte_shop_similar_metrics102: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return (
          clickActionAnalyticsData.syte_shop_similar_metrics102 ||
          pageData.syte_shop_similar_metrics102
        );
      },
    },
    bopis_filter_metrics170: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_filter_metrics170'], '');
      },
    },
    metric139: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event139'], '');
      },
    },
    metric142: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event142'], '');
      },
    },
    metric150: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event150'], '');
      },
    },
    metric151: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event151'], '');
      },
    },
    metric152: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event152'], '');
      },
    },
    metric153: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'event153'], '');
      },
    },
    metric145: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'initiateLiveChat_cm145'], '');
      },
    },
    metric159: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric159'], '');
      },
    },
    metric160: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric160'], '');
      },
    },
    metric62: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric62'], '');
      },
    },
    eVar66: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eVar66'], '');
      },
    },
    metric63: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric63'], '');
      },
    },
    metric81: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric81'], '');
      },
    },
    metric116: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric116'], '');
      },
    },
    eVar19: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eVar19'], '');
      },
    },
    eVar81: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eVar81'], '');
      },
    },
    metric94: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric94'], '');
      },
    },
    eVar178: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eVar178'], '');
      },
    },
    metric33: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric33'], '');
      },
    },
    metric34: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric34'], '');
      },
    },
    metric35: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric35'], '');
      },
    },
    metric36: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric36'], '');
      },
    },
    metric37: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric37'], '');
      },
    },
    relatedSearchTerm: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'relatedSearchTerm'], '');
      },
    },
    searchType: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'searchType'], '');
      },
    },
    bopis_cd50: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_cd50'], '');
      },
    },
    bopis_cd51: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'bopis_cd51'], '');
      },
    },
    slpPage_cd78: {
      get() {
        const { pageData, AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        return clickActionAnalyticsData.slpPage_cd78
          ? clickActionAnalyticsData.slpPage_cd78
          : pageData.slpPage_cd78;
      },
    },
    nav_cd171: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'nav_cd171'], '');
      },
    },
    plp_pagination_cd153: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'plp_pagination_cd153'], '');
      },
    },
    eVar150: {
      get() {
        return store.getState().AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'eVar150'], '');
      },
    },
    login_path_cd123: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'login_path_cd123'], '');
      },
    },
    metric44: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric44'], '');
      },
    },
    metric103: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric103'], '');
      },
    },
    metric82: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric82'], '');
      },
    },
    pickupOption: {
      get() {
        const { AnalyticsDataKey } = store.getState();
        const clickActionAnalyticsData = AnalyticsDataKey.get('clickActionAnalyticsData', {}) || {};
        const pageName = getPageNameValue(store.getState()) || '';
        const isCartTile = clickActionAnalyticsData.isCartTile || false;
        const isBossSwitch = clickActionAnalyticsData.isBossSwitch || false;
        if ((isCartTile && pageName.includes('shopping bag')) || isBossSwitch) {
          return clickActionAnalyticsData.pickupOption || '';
        }
        return '';
      },
    },
    metric41: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric41'], '');
      },
    },
    metric42: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric42'], '');
      },
    },
    metric43: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'metric43'], '');
      },
    },
    expressCheckoutCTAType_cd151: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'expressCheckoutCTAType'], '');
      },
    },
    hpCartItem_cd132: {
      get() {
        return store
          .getState()
          .AnalyticsDataKey.getIn(['clickActionAnalyticsData', 'hpCartItem_cd132'], '');
      },
    },
  });
}
